import { useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { Details, File, FormMarkup, HelpWith } from '@components/basicComponents';
import { ComputeOpsManagementSupportFormFields, getComputeOpsManagementSupportFormData } from '@components/ComputeOpsManagementSupportForm';
import { GreenLakeSupportGLCPFormFields, getGreenLakeSupportGLCPFormData } from '@components/GreenLakeSupportGLCPForm';
import { DSCCFormFields, getDSCCFormData, getDSCCModalData } from '@components/DataServiceCloudConsoleSupportForm';
import { OSQBMSupportFormFields, getOSQBMSupportFormData } from '@components/OSQBMSupportForm';
import { DeveloperPortalSupportFormFields, getDeveloperPortalSupportFormData } from '@components/DeveloperPortalSupportForm';
import { OpsRampFormFields, getOpsRampFormData } from '@components/OpsRampForm';
import { ManagedCloudServiceFormFields, getManagedCloudServiceFormData } from '@components/ManagedCloudService';
import { NetworkingArubaCentralFormFields, getNetworkingArubaCentralFormData } from '@components/NetworkingArubaCentral';
import { DashboardsAndMonitoringFormFields, getDashboardsAndMonitoringFormData } from '@components/DashboardsAndMonitoringForm';
import { PrivateCloudSupportFormFields, getPrivateCloudSupportFormData } from '@components/PrivateCloudSupportForm';
import { GreenLakeCentralSupportFormFields, getGreenLakeCentralSupportFormData } from '@components/GreenLakeCentralSupportForm';
import { getSortedFormFields, handleFormSubmit } from '@utils/forms';
import { HelpWithType } from '@customTypes/enums';
import type { FormExtendedEvent } from 'grommet/components/Form';
import type { FormProps, FormFieldsDetailsProps, ProductProps } from '@customTypes/types';
import type { ConsolidatedGLCPFormState } from '@customTypes/stateTypes';
import { helpWithOptions } from './ConsolidatedGLCPFormOptions';
import { getProductPropsData } from '.';

const formTitle: string = 'HPE GreenLake Platform Support';

export const ConsolidatedGLCPForm = (props: FormProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: ConsolidatedGLCPFormState, newState: Partial<ConsolidatedGLCPFormState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      showDetails: false,
      isRequiredDetails: false,
      numFiles: 0,
    }
  );

  const location = useLocation();

  useEffect(() => {
    const issueParamValue = new URLSearchParams(location.search).get('issue');

    switch (issueParamValue) {
      case 'wuo':
        updateState({ helpWith: helpWithOptions.greenLake });
        break;
      case 'com':
        updateState({ helpWith: helpWithOptions.computeOpsManagement });
        break;
      case 'ds':
        updateState({ helpWith: helpWithOptions.dataServices });
        break;
      case 'bms':
        updateState({ helpWith: helpWithOptions.osqbm });
        break;
      case 'opsramp':
        updateState({ helpWith: helpWithOptions.opsRamp });
        break;
      case 'mcs':
        updateState({ helpWith: helpWithOptions.managedCloudService });
        break;
      case 'nac':
        updateState({ helpWith: helpWithOptions.networkingArubaCentral });
        break;
      case 'dam':
        updateState({ helpWith: helpWithOptions.dashboardsAndMonitoring });
        break;
      case 'pc':
        updateState({ helpWith: helpWithOptions.privateCloud });
        break;
      case 'flex':
        updateState({ helpWith: helpWithOptions.greenLakeFlex });
        break;
      // case 'dp':
      //   updateState({ helpWith: helpWithOptions.developerPortal });
      //   break;
      case 'other':
        updateState({ helpWith: helpWithOptions.other, showDetails: true, isRequiredDetails: true });
        break;
    }
  }, [location]);

  const onDetailsChange = (value: FormFieldsDetailsProps) => {
    updateState(value);
  };

  const onFileChange = (value: number): void => {
    updateState({ numFiles: value });
  };

  const getOnSuccess = (helpWith, helpWithSecondary, helpWithThird) => {
    if (helpWith === helpWithOptions.dataServices) {
      const modalData = getDSCCModalData(helpWithSecondary, helpWithThird);
      return () => props.onSuccess(true, modalData);
    }

    return props.onSuccess;
  }

  const onSubmit = ({ target, value }: FormExtendedEvent) => {
    let namespace: string;
    let subject: string;
    let data;

    let product: ProductProps = getProductPropsData(value.helpWith, value.helpWithSecondary);

    switch (state.helpWith) {
      case helpWithOptions.greenLake:
        data = getGreenLakeSupportGLCPFormData(target, value.helpWithSecondary);
        break;
      case helpWithOptions.computeOpsManagement:
        data = getComputeOpsManagementSupportFormData(target, value.helpWithSecondary, product);
        break;
      case helpWithOptions.dataServices:
        data = getDSCCFormData(target, value.helpWithSecondary, value.helpWithThird, value.companyName, value.severity, value.issueTitle, product);
        break;
      case helpWithOptions.osqbm:
        data = getOSQBMSupportFormData(target, value.helpWithSecondary, value.helpWithThird, value.helpWithFourth, value.countryCode, value.phone);
        break;
      case helpWithOptions.opsRamp:
        data = getOpsRampFormData(target, value.helpWithSecondary);
        break;
      case helpWithOptions.managedCloudService:
        data = getManagedCloudServiceFormData(target, value.helpWithSecondary);
        break;
      case helpWithOptions.networkingArubaCentral:
        data = getNetworkingArubaCentralFormData(target, value.helpWithSecondary);
        break;
      case helpWithOptions.dashboardsAndMonitoring:
        data = getDashboardsAndMonitoringFormData(target, value.helpWithSecondary);
        break;
      case helpWithOptions.privateCloud:
        data = getPrivateCloudSupportFormData(target, value.helpWithSecondary, value.helpWithThird, value.helpWithFourth, value.companyName);
        break;
      case helpWithOptions.greenLakeFlex:
        data = getGreenLakeCentralSupportFormData(target, value.helpWithSecondary, value.helpWithThird);
        break;
      case helpWithOptions.developerPortal:
        data = getDeveloperPortalSupportFormData(target, value.helpWithSecondary, value.helpWithThird);
        break;
      case helpWithOptions.other:
        namespace = 'hpe_greenlake_user';
        subject = `GL Platform Services : ${state.helpWith} - {formId}`;
        data = getSortedFormFields(target, namespace, subject);
        break;
    }

    handleFormSubmit(data, getOnSuccess(value.helpWith, value.helpWithSecondary, value.helpWithThird), props.onError, props.onLoading);
  };

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    updateState({ helpWith: option });

    switch (option) {
      case helpWithOptions.other:
        updateState({ showDetails: true, isRequiredDetails: true });
        break;
      case helpWithOptions.opsRamp:
        updateState({ showDetails: false, isRequiredDetails: true, numFiles: 0 });
        break;
      default:
        updateState({ showDetails: false, isRequiredDetails: false, numFiles: 0 });
    }
  };

  let clearFormFieldsState;

  const assignClearFormFieldsState = (formFieldsClearStateFunc: Function): void => {
    clearFormFieldsState = formFieldsClearStateFunc;
  };

  const onReset = (): void => {
    clearFormFieldsState && clearFormFieldsState();
    updateState({ helpWith: '', showDetails: false, isRequiredDetails: false, numFiles: 0 });
  };

  return (
    <FormMarkup
      onSubmit={onSubmit}
      onReset={onReset}
      formTitle={formTitle}
      doubleFieldPhone
      formFields={
        <>
          <HelpWith
            selectType={HelpWithType.First}
            options={Object.values(helpWithOptions)}
            value={state.helpWith}
            onChange={helpWithChange}
          />

          {state.helpWith === helpWithOptions.greenLake &&
            <GreenLakeSupportGLCPFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.computeOpsManagement &&
            <ComputeOpsManagementSupportFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.dataServices &&
            <DSCCFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.osqbm &&
            <OSQBMSupportFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.privateCloud &&
            <PrivateCloudSupportFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.opsRamp &&
            <OpsRampFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.managedCloudService &&
            <ManagedCloudServiceFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.networkingArubaCentral &&
            <NetworkingArubaCentralFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.dashboardsAndMonitoring &&
            <DashboardsAndMonitoringFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.greenLakeFlex &&
            <GreenLakeCentralSupportFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.helpWith === helpWithOptions.developerPortal &&
            <DeveloperPortalSupportFormFields isConsolidated={true} onDetailsChange={onDetailsChange} onReset={assignClearFormFieldsState} />
          }

          {state.showDetails &&
            <>
              <Details required={state.isRequiredDetails || false} placeholder={'Any additional information we can use to help you, including any error codes or reference ID shown'} />

              <File numFiles={state.numFiles} onFileChange={onFileChange} />
            </>
          }
        </>
      }
    />
  );
};
